import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Html5QrcodeScanner } from "html5-qrcode";
import { QrCode, Lock, User, Eye, EyeOff, Loader2, Wifi } from "lucide-react";
import { toast } from "sonner";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [tab, setTab] = useState("id");
  const [matricule, setMatricule] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const html5Ref = useRef(null);

  const handleLoginMatricule = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { matricule: matricule.trim().toUpperCase() });
      toast.success("Bienvenue " + res.data.name + " !");
      onLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Acces refuse");
    }
    setLoading(false);
  };

  const handleLoginAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      toast.success("Bienvenue Administrateur !");
      onLogin(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mot de passe incorrect");
    }
    setLoading(false);
  };

  const startScan = () => {
    setScanning(true);
  };

  useEffect(() => {
    if (scanning && !html5Ref.current) {
      const timer = setTimeout(() => {
        const el = document.getElementById("qr-login");
        if (el && !html5Ref.current) {
          html5Ref.current = new Html5QrcodeScanner("qr-login", { fps: 10, qrbox: { width: 220, height: 220 }, showTorchButtonIfSupported: true, rememberLastUsedCamera: true }, false);
          html5Ref.current.render(async (decoded) => {
            stopScan();
            setLoading(true);
            try {
              const res = await axios.post(`${API}/auth/login-scan`, { matricule: decoded.trim() });
              toast.success("Bienvenue " + res.data.name + " !");
              onLogin(res.data);
            } catch (err) {
              toast.error(err.response?.data?.detail || "QR non autorise");
            }
            setLoading(false);
          }, () => {});
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [scanning]);

  const stopScan = () => {
    if (html5Ref.current) { html5Ref.current.clear(); html5Ref.current = null; }
    setScanning(false);
  };

  useEffect(() => { return () => { if (html5Ref.current) html5Ref.current.clear(); }; }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <div className="absolute inset-0 rounded-full bg-orange-500 opacity-20 blur-xl scale-150"></div>
            <img src="/logo_scanner.png" alt="Logo" className="relative h-24 w-24 rounded-full object-cover ring-2 ring-orange-400 ring-offset-4 ring-offset-slate-900 mx-auto" />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">ScannerBenj Pro</h1>
          <p className="text-slate-400 mt-1 text-sm">Systeme de pointage intelligent</p>
        </div>

        {/* Card */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-2xl">

          {/* Tabs */}
          <div className="flex rounded-xl bg-white/5 p-1 mb-6 gap-1">
            <button onClick={() => { setTab("id"); stopScan(); }}
              className={"flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 " + (tab === "id" ? "bg-orange-500 text-white shadow" : "text-slate-400 hover:text-white")}>
              <User className="h-4 w-4" />Matricule
            </button>
            <button onClick={() => { setTab("scan"); }}
              className={"flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 " + (tab === "scan" ? "bg-orange-500 text-white shadow" : "text-slate-400 hover:text-white")}>
              <QrCode className="h-4 w-4" />Scan QR
            </button>
            <button onClick={() => { setTab("admin"); stopScan(); }}
              className={"flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center justify-center gap-2 " + (tab === "admin" ? "bg-orange-500 text-white shadow" : "text-slate-400 hover:text-white")}>
              <Lock className="h-4 w-4" />Admin
            </button>
          </div>

          {/* Tab Matricule */}
          {tab === "id" && (
            <form onSubmit={handleLoginMatricule} className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Matricule</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={matricule}
                    onChange={e => setMatricule(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-400 font-mono text-sm"
                    required
                  />
                </div>

              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <User className="h-5 w-5" />}
                {loading ? "Connexion..." : "Se connecter"}
              </button>
            </form>
          )}

          {/* Tab Scan QR */}
          {tab === "scan" && (
            <div className="space-y-4">
              <p className="text-sm text-slate-300 text-center">Scannez votre carte QR de la Régie</p>
              {!scanning ? (
                <button onClick={startScan}
                  className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl flex items-center justify-center gap-2">
                  <QrCode className="h-5 w-5" />Activer la camera
                </button>
              ) : (
                <div>
                  <div ref={scannerRef} id="qr-login" className="rounded-xl overflow-hidden"></div>
                  <button onClick={stopScan} className="w-full mt-3 py-2.5 border border-white/20 text-slate-300 hover:text-white rounded-xl text-sm">Annuler</button>
                </div>
              )}
              {loading && (
                <div className="text-center py-4">
                  <Loader2 className="h-8 w-8 text-orange-500 animate-spin mx-auto" />
                  <p className="text-slate-300 text-sm mt-2">Verification...</p>
                </div>
              )}
            </div>
          )}

          {/* Tab Admin */}
          {tab === "admin" && (
            <form onSubmit={handleLoginAdmin} className="space-y-4">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Mot de passe administrateur</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••••"
                    className="w-full pl-10 pr-12 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-orange-400 text-sm"
                    required
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
                {loading ? "Connexion..." : "Connexion Admin"}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-slate-600 text-xs mt-6">ScannerBenj Pro © 2026</p>
      </div>
    </div>
  );
}